const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December"
]

export function convertSymbolToType(symbol) {
  if (symbol === "%") return "percentage"
  if (symbol === "$") return "amount"
}

export function convertTypeToSymbol(type) {
  type = type.toLowerCase()
  if (type === "percentage") return "%"
  if (type === "percent") return "%"
  if (type === "amount") return "$"
}

export function currencyFormat(amount, defaultValue = "0", maxFractionDigits = 2) {
  // Reject null, undefined, and empty strings
  if (amount === undefined || amount === null || (typeof amount === "string" && amount.trim() === "")) {
    return defaultValue
  }
  // Convert to number and validate
  let numericAmount = Number(amount)
  if (isNaN(numericAmount)) return amount

  // Truncate to given decimal places (no rounding).
  //
  // Bug-fix: previously this was just `Math.trunc(numericAmount * factor) / factor`.
  // `0.29` is stored in IEEE-754 as `0.28999999999999998`, so `0.29 * 100`
  // becomes `28.999999999999996` and `Math.trunc` chops it to 28, producing
  // "0.28" instead of "0.29". Adding a tiny epsilon (scaled to the value's
  // magnitude) absorbs the ~1ulp representation error before truncation.
  // The epsilon is ~2.2e-13 at typical magnitudes — far below any
  // displayable digit at maxFractionDigits ≤ 4, so no genuine truncation
  // case (e.g. 1.999 → 1.99, 0.295 → 0.29) is affected.
  // Only applied to positive values so the negative-number behavior is
  // preserved exactly as it was.
  let factor = Math.pow(10, maxFractionDigits)
  let scaled = numericAmount * factor
  if (scaled > 0) {
    scaled += Number.EPSILON * Math.abs(scaled) * 4
  }
  let truncated = Math.trunc(scaled) / factor
  let [integerPart, decimalPart] = truncated.toFixed(maxFractionDigits).split(".")

  // Format integer part with commas
  integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",")

  // If maxFractionDigits is 0, return only integer part (no decimal point)
  if (maxFractionDigits === 0) {
    return integerPart
  }

  // Keep trailing zeros for consistent decimal display (e.g., 10.00, 10.10)
  // Return final formatted string with fixed decimal places
  return `${integerPart}.${decimalPart}`
}

// export function currencyFormat(amount, defaultValue = 0, maxFractionDigits = 0) {
//
//   if (amount === undefined || amount === null)
//     return defaultValue;
//
//   // Convert the amount to a string.
//   let amountString = amount.toString();
//
//   // Split the string into the integer and decimal parts.
//   let [integerPart, decimalPart] = amountString.split('.');
//
//   // If there is a decimal part and it's longer than two digits, truncate it.
//   if (decimalPart && decimalPart.length > 2) {
//     decimalPart = decimalPart.substring(0, 2);
//   }
//
//   // Format the integer part with commas.
//   integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
//
//
//   // Add zeros to the decimal part if it doesn't exist but maxFractionDigits is greater than 0.
//   if (!decimalPart && maxFractionDigits > 0) {
//     decimalPart = '0'.repeat(maxFractionDigits);
//   } else if (decimalPart && decimalPart.length < maxFractionDigits) {
//     // Add trailing zeros to the decimal part if its length is less than maxFractionDigits.
//     decimalPart = decimalPart + '0'.repeat(maxFractionDigits - decimalPart.length);
//   }
//
//   // Combine the integer part and decimal part if decimal exists and is not '00' or '0'.
//   if ((decimalPart && !/^0+$/g.test(decimalPart)) || (decimalPart && maxFractionDigits != 0)) {
//     return integerPart + '.' + decimalPart;
//   } else {
//     return integerPart;
//   }
//
// }

export function applyFunctionToObject(item, func, excludedKeys = []) {
  for (const key in item) {
    if (!excludedKeys.includes(key)) {
      if (typeof item[key] === "number" || isStringOnlyNumbers(item[key])) {
        item[key] = func(item[key])
      } else if (typeof item[key] === "object") {
        applyFunctionToObject(item[key], func, excludedKeys)
      }
    }
  }
}

export function applyFunctionToNumbers(array, func, excludedKeys = []) {
  for (const item of array) {
    applyFunctionToObject(item, func, excludedKeys)
  }
}

// Advanced formatting with different decimal places for different fields
export function applyFormattingRules(array, rules = {}) {
  const {
    exclude = [],      // Fields to skip completely
    decimals0 = [],    // Fields with 0 decimals (integers): qty, iq_qty, etc.
    decimals2 = [],    // Fields with 2 decimals: total_sales, price, etc.
    decimals4 = []     // Fields with 4 decimals: cost, avg_cost, etc.
  } = rules

  for (const item of array) {
    applyFormattingToObject(item, { exclude, decimals0, decimals2, decimals4 })
  }
}

function applyFormattingToObject(item, rules) {
  for (const key in item) {
    // Skip excluded fields
    if (rules.exclude.includes(key)) {
      continue
    }

    const value = item[key]

    // Check if it's a number or string that looks like a number
    if (typeof value === "number" || isStringOnlyNumbers(value)) {
      // Apply formatting based on field name
      if (rules.decimals0.includes(key)) {
        item[key] = currencyFormat(value, "0", 0)  // No decimals
      } else if (rules.decimals4.includes(key)) {
        item[key] = currencyFormat(value, "0", 4)  // 4 decimals
      } else if (rules.decimals2.includes(key)) {
        item[key] = currencyFormat(value, "0", 2)  // 2 decimals
      } else {
        // Default: 2 decimals for unlisted fields
        item[key] = currencyFormat(value, "0", 2)
      }
    } else if (typeof value === "object" && value !== null) {
      // Recursively apply to nested objects
      applyFormattingToObject(value, rules)
    }
  }
}

export function applyFunctionToStrings(array, func, keys = []) {
  for (const item of array) {
    applyFunctionToObjectString(item, func, keys)
  }
}

export function applyFunctionToObjectString(item, func, keys = []) {
  for (const key in item) {
    if (keys.includes(key)) {
      item[key] = func(item[key])
    }
  }
}

export function isStringOnlyNumbers(inputString) {
  return /^(\d+|\d*\.\d+|\.\d+)$/.test(inputString)
}

export function removeCurrencyFormat(amount, defaultValue = 0) {
  if (!amount) return defaultValue
  const temp = "" + amount
  return temp.replace(/[^0-9.]|(?:\..*)\./g, "")
}

export function formatDateFromServer(date, isDateTime = true) {
  if (date) {
    // FORMAT: Mon dd, yyyy, hh:mm AM
    const d = new Date(date)

    const year = d.getFullYear()
    const month = monthNames[d.getMonth()].substring(0, 3)
    const day = String(d.getDate()).padStart(2, "0")
    const period = hour > 12 ? "PM" : "AM"
    const hour = String(d.getHours() > 12 ? d.getHours() - 12 : d.getHours()).padStart(2, "0")
    const minute = String(d.getMinutes()).padStart(2, "0")

    const formatedDate = `${month} ${day}, ${year}`
    const formatedTime = `${hour}:${minute} ${period}`

    return formatedDate + (isDateTime ? `, ${formatedTime}` : "")
  }
  return ""
}

export function formatDateForServer(inputDate, isDateTime = true) {
  // Get the date string in local time
  const localDateString = new Date(inputDate).toLocaleString("en-US", {
    timeZone: "America/Los_Angeles", // Set timezone to California
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour12: false, // Use 24-hour format
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  })

  // Split the date and time components
  const [date, time] = localDateString.split(", ")

  // Format the date and time separately
  const [month, day, year] = date.split("/")
  const [hour, minute, second] = time.split(":")

  // Construct the formatted date string
  const formattedDate = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`

  // Construct the formatted time string
  const formattedTime = `${hour.padStart(2, "0")}:${minute.padStart(2, "0")}:${second.padStart(2, "0")}`

  // Return the formatted date and time
  return isDateTime ? `${formattedDate} ${formattedTime}` : formattedDate
}

export function getDateRange(dateName = "today", toISOString = true) {
  // Get the current date in the California timezone
  const currentDateCalifornia = new Date().toLocaleString("en-US", { timeZone: "America/Los_Angeles" })
  // Convert the date string to a Date object
  const today = new Date(currentDateCalifornia)

  let daterange = {}

  const dayOfWeek = today.getDay()

  // Calculate the difference in days to go back to the last Monday
  const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1

  switch (dateName) {
    case "today":
      daterange["startDate"] = daterange["endDate"] = today
      break

    case "yesterday":
      today.setDate(today.getDate() - 1)
      daterange["startDate"] = daterange["endDate"] = today
      break

    case "lastWeek":
      const lastMonday = new Date(today)
      lastMonday.setDate(today.getDate() - daysToMonday)

      // Set the start date of the last week to last Monday
      const startDate = new Date(lastMonday)
      startDate.setDate(lastMonday.getDate() - 7)

      // Set the end date of the last week to last Sunday
      const endDate = new Date(lastMonday)
      endDate.setDate(lastMonday.getDate() - 1)

      // Adjust the dates back to UTC if needed
      const startDateUTC = new Date(startDate.getTime() + 8 * 60 * 60 * 1000)
      const endDateUTC = new Date(endDate.getTime() + 8 * 60 * 60 * 1000)

      daterange["startDate"] = startDateUTC
      daterange["endDate"] = endDateUTC
      break

    case "thisWeek":
      const mondayOfCurrentWeek = new Date(today)
      mondayOfCurrentWeek.setDate(today.getDate() - daysToMonday)

      // Calculate the start and end dates of the current week (Monday to Sunday)
      const thisWeekStartDate = new Date(mondayOfCurrentWeek)
      const thisWeekEndDate = new Date(mondayOfCurrentWeek)
      thisWeekEndDate.setDate(mondayOfCurrentWeek.getDate() + 6)

      // Adjust the dates back to UTC if needed
      const thisWeekStartDateUTC = new Date(thisWeekStartDate.getTime() + 8 * 60 * 60 * 1000)
      const thisWeekEndDateUTC = new Date(thisWeekEndDate.getTime() + 8 * 60 * 60 * 1000)

      daterange["startDate"] = thisWeekStartDateUTC
      daterange["endDate"] = thisWeekEndDateUTC
      break

    case "lastMonth":
      daterange["startDate"] = new Date(today.getFullYear(), today.getMonth() - 1, 1)
      daterange["endDate"] = new Date(today.getFullYear(), today.getMonth(), 0)
      break

    case "thisMonth":
      daterange["startDate"] = new Date(today.getFullYear(), today.getMonth(), 1)
      daterange["endDate"] = new Date(today.getFullYear(), today.getMonth() + 1, 0)
      break

    case "lastYear":
      daterange["startDate"] = new Date(today.getFullYear() - 1, 0, 1)
      daterange["endDate"] = new Date(today.getFullYear() - 1, 11, 31)
      break

    case "thisYear":
      daterange["startDate"] = new Date(today.getFullYear(), 0, 1)
      daterange["endDate"] = new Date(today.getFullYear(), 11, 31)
      break

    default:
      daterange = {}
      break
  }

  if (typeof daterange === "object") {
    if (toISOString) {
      daterange.startDate = daterange.startDate.toISOString().split("T")[0]
      daterange.endDate = daterange.endDate.toISOString().split("T")[0]
    }
  }
  return daterange
}
